import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { captionFor } from './tourCaptions';

const KEY = 'veluro_tour_mode';

// Guided Tour Mode (demo accounts only): a quiet caption pill naming the feature on screen as the presenter moves around.
// Clicks pass straight through it (only the × is interactive) so it never hides the button about to be pressed.
// Admin sets the default in Admin → Demo; the presenter can flip it with Shift+Alt+T.
export const GuidedTourBar = () => {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const [enabled, setEnabled] = useState(() => localStorage.getItem(KEY));
  const isDemo = !!user?.demo_account;

  useEffect(() => {
    if (!isDemo || enabled !== null) return;
    api.get('/demo/tour').then((r) => setEnabled(r.data.enabled ? '1' : '0')).catch(() => setEnabled('0'));
  }, [isDemo, enabled]);

  useEffect(() => {
    if (!isDemo) return undefined;
    const onKey = (e) => {
      if (e.shiftKey && e.altKey && e.code === 'KeyT') {
        e.preventDefault();
        setEnabled((v) => { const next = v === '1' ? '0' : '1'; localStorage.setItem(KEY, next); return next; });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDemo]);

  const cap = captionFor(pathname, search, user);
  if (!isDemo || enabled !== '1' || !cap) return null;
  return (
    <div key={cap.title} className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[70] pointer-events-none tour-bar-in" data-testid="guided-tour-bar">
      <div className="flex items-center gap-2.5 max-w-[92vw] sm:max-w-3xl rounded-full border border-white/10 bg-zinc-950/75 backdrop-blur-xl shadow-xl shadow-black/40 pl-2.5 pr-1.5 py-1.5">
        <span className="h-6 w-6 rounded-full bg-violet-500/20 border border-violet-400/30 flex items-center justify-center shrink-0"><Sparkles className="h-3 w-3 text-violet-200" /></span>
        <p className="min-w-0 truncate text-[12.5px] leading-none text-white/85">
          <span className="font-bold uppercase tracking-[0.14em] text-[10px] text-violet-200/90 mr-2" data-testid="guided-tour-title">{cap.title}</span>
          <span data-testid="guided-tour-caption">{cap.caption}</span>
        </p>
        <button type="button" aria-label="Hide tour captions" onClick={() => { localStorage.setItem(KEY, '0'); setEnabled('0'); }} className="pointer-events-auto h-6 w-6 rounded-full text-white/40 hover:text-white hover:bg-white/10 flex items-center justify-center shrink-0" data-testid="guided-tour-hide"><X className="h-3 w-3" /></button>
      </div>
    </div>
  );
};
